import React from 'react'
import GuideLayout from '@/components/help/GuideLayout'
import GuideSection from '@/components/help/GuideSection'
import GuideWidget from '@/components/help/GuideWidget'
import GuideTip from '@/components/help/GuideTip'
import GuideRelatedLinks from '@/components/help/GuideRelatedLinks'
import AdrPickupExplainer from '@/components/help/widgets/AdrPickupExplainer'
import { usePage } from '@inertiajs/react'
import { FALLBACK_BRAND } from '@/lib/brand'
import type { SharedProps } from '@/types/shared'

export default function OccupancyMetrics({ currentUser, properties }: Pick<SharedProps, 'currentUser' | 'properties'>) {
  const brandProp = usePage().props.brand
  const brand = typeof brandProp === 'string' && brandProp.trim() !== '' ? brandProp : FALLBACK_BRAND

  return (
    <GuideLayout
      currentUser={currentUser}
      properties={properties}
      title="Occupancy & Metrics"
      subtitle="Measure what matters — occupancy, ADR, and booking pickup tell you if your strategy is working."
    >
      <GuideSection title="Why Metrics Matter">
        <p className="text-sm leading-relaxed text-muted-foreground">
          You cannot optimise what you do not measure. Revenue management is not just about setting
          rates — it is about <strong className="text-foreground">monitoring the outcome</strong> and
          adjusting.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {brand} tracks three core metrics that, together, give you a complete picture of your
          pricing health.
        </p>
      </GuideSection>

      <GuideSection title="Occupancy">
        <p className="text-sm leading-relaxed text-muted-foreground">
          <strong className="text-foreground">Rooms booked / total rooms available</strong>, shown as
          a percentage. Tracked per-date and per-room-type.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Three demand tiers give you an instant visual read:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-emerald-600">High Demand (80%+)</strong> — you are nearly full.
            Could you have charged more?
          </li>
          <li>
            <strong className="text-amber-600">Moderate (60-79%)</strong> — on track. Monitor pickup
            pace.
          </li>
          <li>
            <strong className="text-slate-500">Low Demand (&lt;60%)</strong> — rooms at risk of going
            unsold. Check if rates are competitive.
          </li>
        </ul>
      </GuideSection>

      <GuideSection title="ADR (Average Daily Rate)">
        <p className="text-sm leading-relaxed text-muted-foreground">
          <strong className="text-foreground">Total room revenue / rooms sold</strong> for a given
          date. This measures revenue quality, not just volume.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Higher ADR means you are earning more per room — but only if occupancy holds. {brand} shows{' '}
          <strong className="text-foreground">current ADR and Last Year (LY) ADR</strong> side by side
          so you can benchmark progress.
        </p>
        <div className="rounded-lg border bg-muted/50 p-4">
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Example:</strong> 4 rooms sold at £80, £95, £110, £120
            = total £405 / 4 = <strong className="text-foreground">ADR of £101.25</strong>
          </p>
        </div>

        <GuideWidget
          title="How Metrics Build Over Time"
          footnote="Watch how ADR and pickup evolve as bookings arrive."
        >
          <AdrPickupExplainer />
        </GuideWidget>
      </GuideSection>

      <GuideSection title="Pickup">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Pickup measures <strong className="text-foreground">how many more (or fewer) rooms have
          been booked</strong> for a date over a recent window: 7 days, 30 days, or 60 days. It is
          counted in rooms, the way you count them.
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-emerald-600">Positive pickup (+2 rooms)</strong> = two more
            rooms booked for that date than a week ago. Demand is building.
          </li>
          <li>
            <strong className="text-slate-500">Flat or negative pickup</strong> = bookings have
            stalled or a guest cancelled. Your rates may be too high for this lead time window.
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          On the occupancy calendar you may also see the same movement expressed in{' '}
          <strong className="text-foreground">occupancy points (pts)</strong> — the share of the
          property those rooms represent (the ADR &amp; pickup explainer above shows it that way).
          Rooms and points are two readings of the one thing: bookings arriving or falling away.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Pickup is the <strong className="text-foreground">most actionable metric</strong> — it tells
          you what is happening now, not what happened in the past.
        </p>
      </GuideSection>

      <GuideSection title="Reading Metrics Together">
        <p className="text-sm leading-relaxed text-muted-foreground">
          No single metric tells the full story. Here is how to read them as a set:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">High occupancy + rising ADR</strong> = ideal. Your
            pricing is working.
          </li>
          <li>
            <strong className="text-foreground">High occupancy + falling ADR</strong> = filling rooms
            but leaving revenue on the table. Consider higher sensitivity or base rates.
          </li>
          <li>
            <strong className="text-foreground">Low occupancy + high ADR</strong> = pricing too high.
            Consider more aggressive demand response or lead time discounts.
          </li>
          <li>
            <strong className="text-foreground">Low occupancy + low ADR</strong> = fundamental
            problem. Review your base rates and competitive positioning.
          </li>
        </ul>
      </GuideSection>

      <GuideSection title="Where to Find Metrics">
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Calendar hover card</strong> — ADR and pickup for any
            date
          </li>
          <li>
            <strong className="text-foreground">Day detail modal</strong> — stat cards for occupancy,
            ADR, pickup 7/30/60d
          </li>
          <li>
            <strong className="text-foreground">Occupancy calendar tab</strong> — full month view of
            demand tiers
          </li>
          <li>
            <strong className="text-foreground">Dashboard</strong> — summary metrics for the property
          </li>
        </ul>
      </GuideSection>

      {/* COMBINED INVENTORY. The one place a room's own figure is not its own listing's: a room the
          building sells on two listings is counted across both, so it says so. */}
      <GuideSection title="Building Figures in a Combined Inventory" id="building-figures">
        <p className="text-sm leading-relaxed text-muted-foreground">
          When one building is sold through several listings, a room the lead listing sells and a following
          listing sells again as a <strong className="text-foreground">Same room</strong> are the same
          physical rooms. Their occupancy is counted once: every listing&apos;s bookings of those rooms over
          the rooms the building has of them. The lead listing&apos;s calendars show the building&apos;s rooms,
          so those rooms are one row there. On a following listing&apos;s Occupancy calendar, which lists the
          lead listing&apos;s room beside its Same room, both carry an{' '}
          <strong className="text-foreground">All listings</strong> label, so you know the figure is not one
          listing&apos;s alone.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Open a night&apos;s day details to see who sold them. The room&apos;s Listings section splits that
          night&apos;s rooms sold by listing, for example{' '}
          <strong className="text-foreground">Wharf House 2 · Wharf House Wing 3 of 6</strong>: two booked on
          the lead listing and three on the following listing, of the building&apos;s six. The figure for the
          whole building is labelled <strong className="text-foreground">Building</strong>, where a hotel on its
          own reads Hotel. A room only one listing sells (an Added room) shows its own figure and no label.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          ADR on the calendar is every listing&apos;s bookings too, so the hover card, the day details and
          the month strip call it <strong className="text-foreground">Building ADR</strong>, and the total
          row of the room pace table reads <strong className="text-foreground">Building</strong> where a
          hotel on its own reads Property.
        </p>
      </GuideSection>

      <GuideSection title="Tips">
        <div className="space-y-3">
          <GuideTip type="do">
            Watch pickup 30d for dates 2-4 weeks out. Flat pickup with low occupancy is an early
            warning — adjust before it is too late.
          </GuideTip>
          <GuideTip type="do">
            Compare ADR to Last Year. If LY ADR was £95 and you are at £85 with similar occupancy,
            your pricing may be too conservative.
          </GuideTip>
          <GuideTip type="dont">
            Do not celebrate high ADR if occupancy is dropping. An empty room at a high rate earns
            zero. RevPAR (revenue per available room) is what matters — and that needs both ADR and
            occupancy.
          </GuideTip>
        </div>
      </GuideSection>

      <GuideRelatedLinks
        links={[
          { slug: 'rate-calendar', title: 'Rate Calendar' },
          { slug: 'demand-response', title: 'Demand Response' },
          { slug: 'dashboard', title: 'Dashboard' },
        ]}
      />
    </GuideLayout>
  )
}
